/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import {
  capitalizeFirstChar,
  compareArrays,
  empty,
  isArray,
  isObject,
  isString,
  prepareResponseData,
} from "../../Utilities/utils";

// css
import "../schools/Schools.css";

// api
import authorizationsApi from "../../api/Authorization";
import subscriptionApi from "../../api/Subscription";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import InputField from "../../components/form/InputField";
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import AppCheckbox from "../../components/form/AppCheckbox";
import SubscriptionsWrapper from "../../components/appWrapper/SubscriptionsWrapper";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import SelectField from "../../components/form/SelectField";
import { yesNo } from "../../data/yesNo";
import EmptyDiv from "../../components/emptyDiv/EmptyDiv";
import { packageTypes } from "../../data/PackageTypes";

const required = "This field is required.";
const validationSchema = Yup.object().shape({
  title: Yup.string().required(required),
  active: Yup.string().required(required),
  package_type: Yup.string().required(required),
});

const AddUpdateSubscriptionPackage = ({ ...props }) => {
  const navigate = useNavigate();
  const { subscriptionPackageId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const toastTR = useRef(null);
  const [selectedAuthorizations, setSelectedAuthorizations] = useState([]);
  const [allItemsChecked, setAllItemsChecked] = useState(false);
  const [schoolAuthorizations, setSchoolAuthorizations] = useState([]);
  const [initialValues, setInitialValues] = useState({
    title: "",
    active: "",
    cost: "",
    package_type: "",
  });

  useEffect(() => {
    getSchoolAuthorizations();
  }, []);

  useEffect(() => {
    if (subscriptionPackageId) {
      getSubscriptionPackageDetails();
    }
  }, [subscriptionPackageId]);

  //alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR.current.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  useEffect(() => {
    const allSelected = compareArrays(
      selectedAuthorizations,
      schoolAuthorizations,
    );
    if (allSelected === true && !empty(schoolAuthorizations)) {
      setAllItemsChecked(true);
    }
  }, [selectedAuthorizations]);

  const onAuthorizationChange = (e) => {
    try {
      const selectedTitle = e.target.value;
      const selectedItem = schoolAuthorizations.find(
        (item) => item.title === selectedTitle,
      );

      setSelectedAuthorizations((prev) => {
        const isSelected = prev.some((item) => item?.title === selectedTitle);
        return isSelected
          ? prev.filter((item) => item?.title !== selectedTitle)
          : [...prev, selectedItem];
      });
    } catch (error) {}
  };

  const onToggleAllRoles = () => {
    if (!allItemsChecked) {
      setSelectedAuthorizations(schoolAuthorizations);
    } else {
      setSelectedAuthorizations([]);
    }
    setAllItemsChecked(!allItemsChecked);
  };

  /**
   * Get School Details
   * @returns
   */
  const getSubscriptionPackageDetails = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const response = await authorizationsApi.getSubscriptionPackageDetails(
        subscriptionPackageId,
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data?.response) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Something went wrong!"
            : "Failed to fetch school details",
        );
      }

      const packageDetails = isObject(response_data?.response)
        ? response_data.response
        : {};
      const { authorizations, title, package_type, active, cost } =
        packageDetails;
      setSelectedAuthorizations(isArray(authorizations) ? authorizations : []);
      return setInitialValues({ title, active, package_type, cost });
    } catch (error) {
      return responseDialog(
        "error",
        error?.code === "ERR_NETWORK" ? "Network Error" : "Error Alert!",
        error?.message || "Something went wrong!",
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get School Authorizations
   * @returns
   */
  const getSchoolAuthorizations = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const response = await authorizationsApi.getAuthorizations();
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data?.response) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Something went wrong!"
            : "Failed to fetch school authorizations",
        );
      }

      setSchoolAuthorizations(
        isArray(response_data?.response) ? response_data.response : [],
      );
    } catch (error) {
      return responseDialog(
        "error",
        error?.code === "ERR_NETWORK" ? "Network Error" : "Error Alert!",
        error?.message || "Something went wrong!",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!isLoading) setIsLoading(true);
    try {
      let response;
      let dataAdded = false;
      let dataUpdated = false;
      if (!subscriptionPackageId) {
        response = await subscriptionApi.createSubscriptionPackage({
          ...values,
          authorizations: selectedAuthorizations,
        });
        dataAdded = true;
      } else {
        response = await subscriptionApi.updateSubscriptionPackage({
          ...values,
          authorizations: selectedAuthorizations,
          subscription_package_id: subscriptionPackageId,
        });
        dataUpdated = true;
      }

      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        const error_response = !empty(response_data.response)
          ? response_data.response
          : "Something went wrong!";
        return responseDialog("error", "Error Alert!", error_response);
      }

      navigate(`/subscription/packages`, {
        state: { dataAdded, dataUpdated },
      });
    } catch (error) {
      return responseDialog(
        "error",
        error?.code === "ERR_NETWORK" ? "Network Error" : "Error Alert!",
        error?.message || "Something went wrong!",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SubscriptionsWrapper {...props}>
        <main>
          <div className="container flex-center-top">
            {/*  header start */}
            <MainHeader title="Create Subscription Package" />
            {/* end of header */}

            <div className="form-container mt-10">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange }) => (
                  <Form style={{ width: "100%" }}>
                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter Package Title"
                        name="title"
                        height={50}
                        labelTitle="Name"
                        required={true}
                      />
                    </div>

                    <div className="mt-30">
                      <AppCheckbox
                        value="All"
                        style={{ marginBottom: 20 }}
                        onChange={onToggleAllRoles}
                        checked={allItemsChecked}
                        width="w-25pc"
                      />
                    </div>
                    {isArray(schoolAuthorizations) &&
                    !empty(schoolAuthorizations)
                      ? Array(Math.ceil(schoolAuthorizations.length / 4))
                          .fill()
                          .map((_, rowIndex) => (
                            <div
                              key={`row-${rowIndex}`}
                              className="flex flex-wrap justify-content-center gap-3 w-100p mb-20"
                            >
                              {schoolAuthorizations
                                .slice(rowIndex * 4, (rowIndex + 1) * 4)
                                .map((item, itemIndex) => {
                                  const globalIndex = rowIndex * 4 + itemIndex;
                                  return (
                                    <AppCheckbox
                                      key={globalIndex}
                                      index={globalIndex}
                                      value={item?.title}
                                      width="w-25pc"
                                      onChange={onAuthorizationChange}
                                      checked={selectedAuthorizations.some(
                                        (auth) =>
                                          auth?.machine_name ===
                                          item?.machine_name,
                                      )}
                                    />
                                  );
                                })}
                            </div>
                          ))
                      : ""}

                    <div className="app_input_group">
                      <InputField
                        placeholder="Enter cost per Unit"
                        name="cost"
                        height={50}
                        labelTitle="Cost per unit"
                        required={true}
                      />
                      <EmptyDiv />
                      <EmptyDiv />
                    </div>

                    <div className="app_input_group">
                      <SelectField
                        labelTitle="Package Type"
                        required={true}
                        placeholder="Select Package Type"
                        name="package_type"
                        options={packageTypes}
                        height={50}
                        valueKey="title"
                        selectedOption={
                          isString(values.package_type)
                            ? values.package_type
                            : values.package_type
                        }
                        handleChangeFunc={handleChange}
                      />
                      <EmptyDiv />
                      <EmptyDiv />
                    </div>

                    <div className="app_input_group">
                      <SelectField
                        labelTitle="Active"
                        required={true}
                        placeholder="Activate this School?"
                        name="active"
                        options={yesNo}
                        height={50}
                        valueKey="title"
                        selectedOption={
                          isString(values.active)
                            ? capitalizeFirstChar(values.active)
                            : values.active
                        }
                        handleChangeFunc={handleChange}
                      />
                      <EmptyDiv />
                      <EmptyDiv />
                    </div>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 40,
                      }}
                    >
                      <ButtonIcon
                        height={45}
                        marginTop={5}
                        color="#ffffff"
                        backgroundColor="#633ccd"
                        width={300}
                        borderColor="#633ccd"
                        buttonText={
                          subscriptionPackageId
                            ? "Update Subscription Package"
                            : "Create Subscription Package"
                        }
                        type="submit"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}
        <Toast ref={toastTR} position="bottom-left" />
      </SubscriptionsWrapper>
    </>
  );
};

export default AddUpdateSubscriptionPackage;
